import { EpisodeList } from "../components/EpisodeList.js";
import { WatchCTA } from "../components/WatchCTA.js";
import { getWholeAgeAtDate } from "../lib/dateUtils.js";
import { getActorAgeData, getShowMetadata, renderShowContent } from "../lib/seoContent.js";
import {
  getEpisodeCast,
  getPilotEpisode,
} from "../lib/dataStore.js";
import {
  formatEpisodeCode,
  formatDate,
} from "../lib/formatters.js";
import { getImdbHref } from "../lib/personUtils.js";

const STAR_SVG = `<svg class="show-rating-star" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
  <path d="M8 .25a.75.75 0 0 1 .673.418l1.882 3.815 4.21.612a.75.75 0 0 1 .416 1.279l-3.046 2.97.719 4.192a.75.75 0 0 1-1.088.791L8 12.347l-3.766 1.98a.75.75 0 0 1-1.088-.79l.72-4.194L.818 6.374a.75.75 0 0 1 .416-1.28l4.21-.611L7.327.668A.75.75 0 0 1 8 .25z"/>
</svg>`;
const BATCH_SIZE = 10;

function getObjectPositionY(position, fallback = "30%") {
  if (typeof position !== "string") return fallback;
  const parts = position.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  if (parts.length === 1) return parts[0];
  return parts[1];
}

function buildEpisodeStrip(episode) {
  const code = formatEpisodeCode(episode.season, episode.episode);
  const div = document.createElement("div");
  div.className = "show-episode-strip";
  div.innerHTML = `
    <span class="tag">${code}</span>
    <span class="show-ep-title">${episode.title}</span>
    <span class="show-ep-meta">Originally aired ${formatDate(episode.airDate)}</span>
  `;
  return div;
}

function ageDecadeClass(age) {
  if (!Number.isFinite(age)) return "";
  if (age < 20) return "age-teens";
  if (age < 30) return "age-20s";
  if (age < 40) return "age-30s";
  if (age < 50) return "age-40s";
  return "age-50s";
}

function buildInlineMetadata(parts) {
  return parts.reduce((items, part) => {
    if (!part) return items;
    if (items.length) {
      items.push(`<span class="cast-inline-sep" aria-hidden="true">·</span>`);
    }
    items.push(part);
    return items;
  }, []).join("");
}

function getPersonAge(show, person, airDate) {
  return getWholeAgeAtDate(getActorAgeData(show.id, person).dob, airDate);
}

function buildCastRow(person, airDate, show, { referenceEpisodeCode = "" } = {}) {
  const effectiveAirDate = person.displayAirDate ?? airDate;
  const age = getPersonAge(show, person, effectiveAirDate);
  const ageWhole = age ?? "—";
  const decadeClass = ageDecadeClass(age);
  const runtimeLabel = referenceEpisodeCode
    ? `<span class="cast-runtime">${referenceEpisodeCode}</span>`
    : "";
  const infoRow1 = buildInlineMetadata([
    runtimeLabel,
    person.birthplace
      ? `<span class="cast-birthplace">${person.birthplace}</span>`
      : "",
  ]);

  const rightRow2 = person.knownFor
    ? `<span class="cast-known-for">${person.knownFor}</span>`
    : "";

  return `
    <li class="cast-row">
      <button class="cast-row-main" type="button" aria-label="Actions for ${person.name}">
        <span class="cast-col cast-col--name">
          <span class="cast-name">${person.name}</span>
          <span class="cast-character">${person.character ?? ""}</span>
        </span>
        <span class="cast-col cast-col--age ${decadeClass}"${age === null ? ' title="Birth date not verified" aria-label="Age unavailable: birth date not verified"' : ""}>${ageWhole}<span class="cast-age-unit">yrs</span></span>
        <span class="cast-col cast-col--info">
          ${infoRow1 ? `<span class="cast-info-row1">${infoRow1}</span>` : ""}
          ${rightRow2 ? `<span class="cast-info-row2">${rightRow2}</span>` : ""}
        </span>
      </button>
    </li>
  `;
}

function getCelebrityAppearances(show) {
  const byKey = new Map();

  const ensureEntry = (person, episode) => {
    const key = getPersonKey(person);
    if (!key) return null;

    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        person: {
          ...person,
          id: person.id ?? key,
        },
        appearances: [],
      });
    }

    const entry = byKey.get(key);
    const episodeKey = episode.id;
    if (!entry.appearances.some((appearance) => appearance.id === episodeKey)) {
      entry.appearances.push({
        id: episode.id,
        season: episode.season,
        episode: episode.episode,
        airDate: episode.airDate,
      });
    }
    return entry;
  };

  show.episodes.forEach((episode) => {
    (episode.cameos ?? []).forEach((cameo) => {
      ensureEntry(cameo, episode);
    });
  });

  return [...byKey.values()]
    .map((entry) => {
      const appearances = entry.appearances.sort((left, right) =>
        left.airDate.localeCompare(right.airDate),
      );
      const displayEpisodeCodes = appearances
        .slice(0, 2)
        .map((appearance) => formatEpisodeCode(appearance.season, appearance.episode))
        .join(", ");

      return {
        ...entry.person,
        appearanceCount: appearances.length,
        appearances,
        displayEpisodeCodes,
      };
    })
    .sort((left, right) => {
      const leftAirDate = left.appearances[0]?.airDate ?? "";
      const rightAirDate = right.appearances[0]?.airDate ?? "";
      if (leftAirDate !== rightAirDate) {
        return leftAirDate.localeCompare(rightAirDate);
      }
      return left.name.localeCompare(right.name);
    });
}

function createRowElement(person, airDate, show, episode, options = {}) {
  const referenceEpisode = person.referenceEpisodeId
    ? show.episodes.find((item) => item.id === person.referenceEpisodeId) ?? episode
    : episode;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = buildCastRow(person, airDate, show, { ...options, referenceEpisodeCode: formatEpisodeCode(referenceEpisode.season, referenceEpisode.episode) }).trim();
  const li = wrapper.firstElementChild;

  const rowBtn = li.querySelector(".cast-row-main");
  rowBtn.addEventListener("click", async () => {
    rowBtn.disabled = true;
    rowBtn.setAttribute("aria-busy", "true");
    try {
      const { openActorActionSheet } = await import("../components/ShareModal.js");
      if (!li.isConnected) return;
      const age = getPersonAge(show, person, person.displayAirDate ?? airDate);
      // Build episode cast for "whole cast" card, ensuring this actor is included
      const episodeCast = getEpisodeCast(show, referenceEpisode).map((p) => ({
        name: p.name,
        actorName: p.name,
        character: p.character ?? "",
        age: getPersonAge(show, p, referenceEpisode.airDate),
        decadeClass: ageDecadeClass(getPersonAge(show, p, referenceEpisode.airDate)),
        key: p.id ?? p.name,
      })).filter((person) => Number.isFinite(person.age));
      const thisKey = person.id ?? person.name;
      const inTop5 = episodeCast.slice(0, 5).some((p) => p.key === thisKey);
      let top5Cast;
      if (inTop5 || episodeCast.length <= 5) {
        top5Cast = episodeCast.slice(0, 5);
      } else {
        // Replace 5th slot with this actor
        const thisActor = {
          name: person.name,
          actorName: person.name,
          character: person.character ?? "",
          age,
          decadeClass: ageDecadeClass(age),
          key: thisKey,
        };
        top5Cast = [...episodeCast.slice(0, 4), thisActor];
      }
      openActorActionSheet({
        person,
        age,
        decadeClass: ageDecadeClass(age),
        imdbHref: getImdbHref(person),
        show,
        episode: referenceEpisode,
        top5Cast,
        allActors: episodeCast,
      });
    } catch {
      if (!li.querySelector(".actor-load-error")) {
        const fallback = document.createElement("p");
        fallback.className = "show-age-note actor-load-error";
        fallback.setAttribute("role", "status");
        fallback.append("Actor details could not load. ");
        const profile = document.createElement("a");
        profile.href = getImdbHref(person);
        profile.target = "_blank";
        profile.rel = "noopener noreferrer";
        profile.textContent = "Open IMDb profile";
        fallback.append(profile);
        li.append(fallback);
      }
    } finally {
      rowBtn.disabled = false;
      rowBtn.removeAttribute("aria-busy");
    }
  });

  return li;
}

function createCastSection(
  title,
  people,
  airDate,
  show,
  episode,
  options = {},
) {
  const section = document.createElement("section");
  section.className = "cast-section";
  const infoHeaderPrimary = options.infoHeaderPrimary ?? "Age reference";
  const infoHeaderSecondary = options.infoHeaderSecondary ?? "Hometown";
  section.innerHTML = `
    <div class="cast-section-header">
      <span class="cast-section-title cast-col cast-col--name">${title}</span>
      <span class="cast-col cast-col--age"></span>
      <span class="cast-section-metadata cast-col cast-col--info">
        <span>${infoHeaderPrimary}</span>
        <span class="cast-header-sep" aria-hidden="true">·</span>
        <span>${infoHeaderSecondary}</span>
      </span>
    </div>
  `;

  if (options.warningMessage) {
    const warning = document.createElement("div");
    warning.className = "cast-section-warning";
    warning.textContent = options.warningMessage;
    section.append(warning);
  }

  const list = document.createElement("ol");
  list.className = "cast-list";
  list.setAttribute("aria-live", "polite");

  const button = document.createElement("button");
  button.className = "load-more-btn";
  button.type = "button";
  function updateButtonText() {
    const remaining = people.length - visible;
    button.textContent = `Load ${remaining} more`;
  }

  let visible = 0;

  function appendBatch() {
    const batch = people.slice(visible, visible + BATCH_SIZE);
    const fragment = document.createDocumentFragment();
    batch.forEach((person) =>
      fragment.append(createRowElement(person, airDate, show, episode, options)),
    );
    list.append(fragment);
    visible += batch.length;
    const done = visible >= people.length;
    button.hidden = done;
    if (!done) updateButtonText();
  }

  button.addEventListener("click", appendBatch);
  appendBatch();

  section.append(list);
  if (visible < people.length) {
    section.append(button);
  }

  return section;
}

function getPersonKey(person) {
  return person.id ?? person.name;
}

function dedupePeople(people) {
  const seen = new Set();
  return people.filter((person) => {
    const key = getPersonKey(person);
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function buildCastSections(show, episode, celebrityAppearances) {
  const airDate = episode.airDate;
  const episodePeople = dedupePeople(getEpisodeCast(show, episode));
  const episodeKeys = new Set(episodePeople.map(getPersonKey));
  const recurringPeople = show.actors
    .filter((actor) => actor && !episodeKeys.has(actor.id))
    .filter((actor) => actor.castSection !== "cameo")
    .sort((left, right) => right.minutes - left.minutes);

  const recurringKeys = new Set(recurringPeople.map(getPersonKey));

  const explicitShowCelebrities = show.actors
    .filter((actor) => actor?.castSection === "cameo")
    .filter((actor) => !episodeKeys.has(actor.id))
    .filter((actor) => !recurringKeys.has(actor.id));
  const celebrityPeople = dedupePeople([
    ...celebrityAppearances
      .filter((person) => !episodeKeys.has(getPersonKey(person)))
      .filter((person) => !recurringKeys.has(getPersonKey(person))),
    ...explicitShowCelebrities,
  ]);
  const fragment = document.createDocumentFragment();
  fragment.append(
    createCastSection("Actor ages on this air date", episodePeople, airDate, show, episode),
  );
  if (recurringPeople.length) {
    fragment.append(
      createCastSection(
        "Other cast · ages on selected air date",
        recurringPeople,
        airDate,
        show,
        episode,
      ),
    );
  }
  if (celebrityPeople.length) {
    fragment.append(
      createCastSection(
        "Guest cast · ages on selected air date",
        celebrityPeople,
        airDate,
        show,
        episode,
        {
          infoHeaderPrimary: "Age reference",
          infoHeaderSecondary: "Hometown",
        },
      ),
    );
  }
  return fragment;
}

export function ShowPage({ show, season = null, episodeId = null }) {
  const element = document.createElement("section");
  element.className = "page-shell show-page-full";

  const seasonEpisodes = season === null ? show.episodes : show.episodes.filter((episode) => episode.season === season);
  const pilotEpisode = seasonEpisodes[0] ?? getPilotEpisode(show);
  const selectedEpisode = seasonEpisodes.find((episode) => episode.id === episodeId) ?? pilotEpisode;
  const metadata = getShowMetadata(show, season);
  const celebrityAppearances = getCelebrityAppearances(show);

  // ── 1. Hero ──────────────────────────────────────────────────
  const genrePills = (show.genres ?? [])
    .map((g) => `<span class="show-genre-pill">${g}</span>`)
    .join("");

  const ratingHtml = show.imdbRating
    ? `<div class="show-hero-rating">${STAR_SVG}<span class="show-rating-value">${show.imdbRating}</span><span class="show-rating-denom">/10</span></div>`
    : "";

  // ── 2. Meta bar ─────────────────────────────────────────────
  const metaParts = ["TV Series", show.years.replace("-", "–")];
  if (show.contentRating) metaParts.push(show.contentRating);
  if (show.runtime) metaParts.push(`${show.runtime}m`);

  const ratingCountHtml = show.imdbRatingCount
    ? `<span>${show.imdbRatingCount} ratings</span>`
    : "";
  const popularityHtml = show.imdbPopularity
    ? `<span class="show-popularity-tag">#${show.imdbPopularity} popularity</span>`
    : "";
  const mobileObjectPositionY = getObjectPositionY(
    show.homeHero?.objectPosition ?? show.hero.objectPosition,
  );

  // ── 3. Episode strip + cast (updated on selection) ──────────
  const episodeStripEl = document.createElement("div");
  episodeStripEl.className = "show-dynamic";
  episodeStripEl.setAttribute("aria-describedby", "show-age-note");

  function renderEpisode(episode) {
    const strip = buildEpisodeStrip(episode);
    episodeStripEl.replaceChildren(strip, buildCastSections(show, episode, celebrityAppearances));
  }

  if (selectedEpisode) renderEpisode(selectedEpisode);

  const onSelectEpisode = (episode) => {
    renderEpisode(episode);
    // Preserve the selected episode on refresh and when sharing a URL.
    const url = new URL(window.location.href);
    url.searchParams.set("episode", episode.id);
    history.replaceState(history.state, "", `${url.pathname}${url.search}${url.hash}`);
  };

  // ── Assemble ─────────────────────────────────────────────────
  element.innerHTML = `
    <div class="show-header">
      <picture class="show-header-media" style="--op-mobile-y:${mobileObjectPositionY};--op-desktop:${show.hero.objectPosition};">
        ${
          show.homeHero
            ? `<source
        media="(max-width: 499px)"
        srcset="${show.homeHero.src}"
      />`
            : ""
        }
        <img
          class="show-header-img"
          src="${show.hero.src}"
          alt="${show.title} promotional image"
        />
      </picture>
      <div class="show-header-info">
        <h1 class="show-header-title">${show.title}${season === null ? "" : ` · Season ${season}`} cast ages</h1>
        <div class="show-genre-pills">${genrePills}</div>
        <div class="show-header-rating">
          ${ratingHtml}
          <span class="show-meta-format">${metaParts.join(" · ")}</span>
        </div>
        ${
          show.imdbRatingCount || show.imdbPopularity
            ? `
          <div class="show-header-stats">
            ${popularityHtml}
            ${ratingCountHtml && popularityHtml ? `<span class="show-stats-sep">·</span>` : ""}
            ${ratingCountHtml}
          </div>
        `
            : ""
        }
        <p class="show-description">${metadata.description}</p>
      </div>
    </div>

    <div class="show-picker-row"></div>
    <p class="show-age-note" id="show-age-note">Ages use the selected episode’s original air date, not filming dates or fictional character ages. Cast lists are show rosters and may not confirm every actor’s appearance in that episode. A dash means the birth date is unverified or conflicting.</p>
  `;

  // Inject Watch CTA into header info after description
  const cta = WatchCTA(show);
  if (cta) element.querySelector(".show-header-info").append(cta);

  // Append episode picker into the picker row slot
  const picker = EpisodeList(season === null ? show : { ...show, episodes: seasonEpisodes }, selectedEpisode?.id ?? null, onSelectEpisode);
  element.querySelector(".show-picker-row").append(picker);

  // Append dynamic episode strip + cast section
  element.append(episodeStripEl);
  const overview = document.createElement("div");
  overview.className = "show-age-content";
  overview.innerHTML = renderShowContent(show, season);
  element.append(overview);

  return {
    title: metadata.title,
    description: metadata.description,
    destroy: () => picker.destroy?.(),
    element,
  };
}
